import React, { useState, useEffect, useRef } from 'react';
import {
  Bell,
  BellRing,
  BellOff,
  Volume2,
  VolumeX,
  CheckCircle2,
  Trash2,
  Calendar,
  ShoppingBag,
  DollarSign,
  Zap,
  X,
  ExternalLink,
  Sparkles,
  AlertTriangle
} from 'lucide-react';

// Generador de sonidos nativo (Web Audio API - Sin archivos externos, cero latencia)
export const playNotificationSound = (type = 'default') => {
  try {
    const isMuted = localStorage.getItem('seryi_sound_enabled') === 'false';
    if (isMuted) return;

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;

    const ctx = new AudioContextClass();

    const playTone = (freq, startTime, duration, typeOsc = 'sine', gainVal = 0.15) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = typeOsc;
      osc.frequency.setValueAtTime(freq, ctx.currentTime + startTime);

      gain.gain.setValueAtTime(0, ctx.currentTime + startTime);
      gain.gain.linearRampToValueAtTime(gainVal, ctx.currentTime + startTime + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + startTime + duration);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(ctx.currentTime + startTime);
      osc.stop(ctx.currentTime + startTime + duration);
    };

    if (type === 'appointment') {
      // Campanita elegante de 2 tonos ascendentes (C5 -> G5)
      playTone(523.25, 0, 0.25, 'triangle', 0.2);
      playTone(783.99, 0.15, 0.4, 'triangle', 0.25);
    } else if (type === 'order') {
      // Alerta cálida de cocina (3 tonos: E5 -> G5 -> C6)
      playTone(659.25, 0, 0.15, 'sine', 0.2);
      playTone(783.99, 0.12, 0.15, 'sine', 0.2);
      playTone(1046.50, 0.24, 0.45, 'sine', 0.25);
    } else if (type === 'payment') {
      // Tono triunfal de moneda (G5 -> E6)
      playTone(783.99, 0, 0.18, 'sine', 0.2);
      playTone(1318.51, 0.12, 0.5, 'triangle', 0.25);
    } else {
      // Beep suave estándar
      playTone(880, 0, 0.3, 'sine', 0.15);
    }
  } catch (err) {
    console.warn('AudioContext autoplay error o no soportado:', err);
  }
};

// Enviar notificación Push del sistema operativo (Móvil / Escritorio)
export const triggerSystemNotification = (title, body, type = 'default') => {
  playNotificationSound(type);

  if ('Notification' in window && Notification.permission === 'granted') {
    try {
      const n = new Notification(title, {
        body: body,
        icon: '/icono-seryiads.png',
        badge: '/icono-seryiads.png',
        tag: 'seryi-alert-' + Date.now(),
        requireInteraction: false
      });

      n.onclick = () => {
        window.focus();
        n.close();
      };
    } catch (err) {
      console.warn('No se pudo abrir notificación del sistema:', err);
    }
  }
};

export default function NotificationCenter({
  isOpen,
  onClose,
  notifications = [],
  onMarkAllRead,
  onClearAll,
  onNotificationClick,
  onAddDemoNotification,
  activeBusiness
}) {
  const [filter, setFilter] = useState('all'); // all, unread, appointment, order, system
  const [permission, setPermission] = useState(
    typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'default'
  );
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return localStorage.getItem('seryi_sound_enabled') !== 'false';
  });

  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
  }, [isOpen]);

  const toggleSound = () => {
    const newVal = !soundEnabled;
    setSoundEnabled(newVal);
    localStorage.setItem('seryi_sound_enabled', String(newVal));
    if (newVal) {
      playNotificationSound('payment');
    }
  };

  const handleRequestPermission = async () => {
    if (!('Notification' in window)) {
      alert('Tu navegador actual no soporta notificaciones nativas de escritorio.');
      return;
    }
    try {
      const res = await Notification.requestPermission();
      setPermission(res);
      if (res === 'granted') {
        triggerSystemNotification(
          '¡Alertas Push Activadas con Éxito!',
          `Recibirás alertas en tiempo real de citas y pedidos de ${activeBusiness?.nombre_negocio || 'Keyla CRM'}.`,
          'payment'
        );
      }
    } catch (err) {
      console.error('Error pidiendo permiso de notificación:', err);
    }
  };

  const handleTestAlert = (type) => {
    if (type === 'appointment') {
      const title = '🗓️ ¡Nueva Cita Registrada!';
      const body = 'Carlos Gómez agendó "Corte y Barba" para Hoy a las 4:30 PM.';
      if (onAddDemoNotification) {
        onAddDemoNotification({
          id: Date.now(),
          type: 'appointment',
          title,
          body,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          read: false
        });
      }
      triggerSystemNotification(title, body, 'appointment');
    } else if (type === 'order') {
      const title = '🍔 ¡Nuevo Pedido de WhatsApp!';
      const body = 'Orden #108 • 2x Hamburguesas Artesanales - $52,000 COP.';
      if (onAddDemoNotification) {
        onAddDemoNotification({
          id: Date.now(),
          type: 'order',
          title,
          body,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          read: false
        });
      }
      triggerSystemNotification(title, body, 'order');
    } else {
      const title = '💰 Comprobante de Pago Recibido';
      const body = 'Cliente adjuntó URL de pago para revisión y renovación.';
      if (onAddDemoNotification) {
        onAddDemoNotification({
          id: Date.now(),
          type: 'payment',
          title,
          body,
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          read: false
        });
      }
      triggerSystemNotification(title, body, 'payment');
    }
  };

  const filteredList = notifications.filter((item) => {
    if (filter === 'unread') return !item.read;
    if (filter === 'appointment') return item.type === 'appointment';
    if (filter === 'order') return item.type === 'order';
    if (filter === 'system') return item.type === 'system' || item.type === 'payment';
    return true;
  });

  const unreadCount = notifications.filter((n) => !n.read).length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="w-full max-w-md bg-seryi-bg-dark border-l border-seryi-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-300">
        
        {/* CABECERA DEL CAJÓN */}
        <div className="p-5 border-b border-seryi-border flex items-center justify-between bg-seryi-bg/80 backdrop-blur-md">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-seryi-green/20 border border-seryi-green/30 flex items-center justify-center text-seryi-green">
              {unreadCount > 0 ? <BellRing size={18} className="animate-bounce" /> : <Bell size={18} />}
            </div>
            <div>
              <h3 className="font-extrabold text-sm text-seryi-heading flex items-center gap-2">
                Centro de Alertas & Push
                {unreadCount > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 border border-red-500/30 text-[10px] font-bold">
                    {unreadCount} nuevas
                  </span>
                )}
              </h3>
              <p className="text-[11px] text-seryi-text">
                Monitor en tiempo real para {activeBusiness?.nombre_negocio || 'Keyla CRM'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-1.5">
            <button
              onClick={toggleSound}
              title={soundEnabled ? 'Sonido Activado' : 'Sonido Silenciado'}
              className={`p-2 rounded-xl border transition-all ${
                soundEnabled
                  ? 'bg-seryi-green/10 border-seryi-green/30 text-seryi-green hover:bg-seryi-green/20'
                  : 'bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20'
              }`}
            >
              {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-xl border border-seryi-border text-seryi-text hover:text-white hover:bg-white/5 transition-all"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* BARRA DE ESTADO Y PERMISOS PUSH NATIVOS */}
        <div className="px-5 py-3 border-b border-seryi-border bg-seryi-bg-light/40 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs">
            {permission === 'granted' ? (
              <span className="flex items-center gap-1.5 text-seryi-green font-semibold">
                <CheckCircle2 size={14} /> Push activado en dispositivo
              </span>
            ) : permission === 'denied' ? (
              <span className="flex items-center gap-1.5 text-red-400 font-semibold">
                <BellOff size={14} /> Permisos bloqueados
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-amber-400 font-semibold">
                <AlertTriangle size={14} /> Alertas de SO pendientes
              </span>
            )}
          </div>
          {permission !== 'granted' && permission !== 'denied' && (
            <button
              onClick={handleRequestPermission}
              className="px-3 py-1.5 rounded-lg bg-seryi-green text-seryi-bg-dark font-extrabold text-[11px] hover:bg-seryi-green/80 transition-all shadow-md active:scale-95"
            >
              Activar Alertas 🔔
            </button>
          )}
        </div>

        {/* PESTAÑAS DE FILTRO Y BOTÓN DE ACCIONES */}
        <div className="px-5 py-2.5 border-b border-seryi-border flex items-center justify-between gap-2 overflow-x-auto">
          <div className="flex items-center gap-1">
            {[
              { id: 'all', label: 'Todas' },
              { id: 'unread', label: 'No Leídas' },
              { id: 'appointment', label: 'Citas' },
              { id: 'order', label: 'Pedidos' }
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setFilter(t.id)}
                className={`px-3 py-1 rounded-lg text-xs font-bold transition-all ${
                  filter === t.id
                    ? 'bg-seryi-green text-seryi-bg-dark shadow'
                    : 'text-seryi-text hover:text-white hover:bg-white/5'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {notifications.length > 0 && (
            <div className="flex items-center gap-1">
              <button
                onClick={onMarkAllRead}
                title="Marcar todas como leídas"
                className="p-1.5 text-seryi-text hover:text-seryi-green transition-colors"
              >
                <CheckCircle2 size={15} />
              </button>
              <button
                onClick={onClearAll}
                title="Limpiar todas"
                className="p-1.5 text-seryi-text hover:text-red-400 transition-colors"
              >
                <Trash2 size={15} />
              </button>
            </div>
          )}
        </div>

        {/* SIMULADOR EN VIVO PARA DEMOSTRACIONES */}
        <div className="px-5 py-3 border-b border-seryi-border bg-gradient-to-r from-blue-900/10 via-seryi-bg-light/20 to-purple-900/10">
          <p className="text-[10px] font-extrabold uppercase tracking-wider text-seryi-text flex items-center gap-1 mb-2">
            <Sparkles size={12} className="text-amber-400" /> Simular Alerta en Tiempo Real (Demo / Sonido)
          </p>
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => handleTestAlert('appointment')}
              className="px-2.5 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-300 hover:bg-blue-500/20 font-bold text-[11px] flex items-center justify-center gap-1 transition-all"
            >
              <Calendar size={12} /> +Cita
            </button>
            <button
              onClick={() => handleTestAlert('order')}
              className="px-2.5 py-1.5 rounded-lg bg-purple-500/10 border border-purple-500/20 text-purple-300 hover:bg-purple-500/20 font-bold text-[11px] flex items-center justify-center gap-1 transition-all"
            >
              <ShoppingBag size={12} /> +Pedido
            </button>
            <button
              onClick={() => handleTestAlert('payment')}
              className="px-2.5 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 hover:bg-emerald-500/20 font-bold text-[11px] flex items-center justify-center gap-1 transition-all"
            >
              <DollarSign size={12} /> +Pago
            </button>
          </div>
        </div>

        {/* LISTADO DE NOTIFICACIONES */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
          {filteredList.length === 0 ? (
            <div className="h-48 flex flex-col items-center justify-center text-center p-6 border border-dashed border-seryi-border rounded-2xl">
              <BellOff size={32} className="text-seryi-text/40 mb-2" />
              <p className="text-sm font-bold text-seryi-heading">Sin alertas pendientes</p>
              <p className="text-xs text-seryi-text mt-0.5">
                Las citas y pedidos que ingresen por WhatsApp o el portal aparecerán aquí al instante.
              </p>
            </div>
          ) : (
            filteredList.map((item) => {
              const isUnread = !item.read;
              const IconComp =
                item.type === 'appointment'
                  ? Calendar
                  : item.type === 'order'
                  ? ShoppingBag
                  : item.type === 'payment'
                  ? DollarSign
                  : Zap;

              const badgeColor =
                item.type === 'appointment'
                  ? 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                  : item.type === 'order'
                  ? 'bg-purple-500/10 text-purple-400 border-purple-500/20'
                  : item.type === 'payment'
                  ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                  : 'bg-amber-500/10 text-amber-400 border-amber-500/20';

              return (
                <div
                  key={item.id}
                  onClick={() => {
                    if (onNotificationClick) onNotificationClick(item);
                  }}
                  className={`p-3.5 rounded-2xl border transition-all cursor-pointer ${
                    isUnread
                      ? 'bg-seryi-bg border-seryi-green/40 shadow-lg shadow-seryi-green/5'
                      : 'bg-seryi-bg-light/30 border-seryi-border opacity-75 hover:opacity-100'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2.5 rounded-xl border shrink-0 ${badgeColor}`}>
                      <IconComp size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="font-extrabold text-xs text-seryi-heading truncate">
                          {item.title}
                        </span>
                        <span className="text-[10px] text-seryi-text shrink-0">
                          {item.timestamp}
                        </span>
                      </div>
                      <p className="text-xs text-seryi-text leading-relaxed line-clamp-2">
                        {item.body}
                      </p>
                      {item.actionLabel && (
                        <div className="mt-2 flex items-center gap-1.5 text-[11px] font-bold text-seryi-green hover:underline">
                          <span>{item.actionLabel}</span>
                          <ExternalLink size={11} />
                        </div>
                      )}
                    </div>
                    {isUnread && (
                      <div className="w-2 h-2 rounded-full bg-seryi-green shrink-0 mt-1" />
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* PIE DEL CAJÓN */}
        <div className="p-4 border-t border-seryi-border bg-seryi-bg-light/30 flex items-center justify-between text-xs text-seryi-text">
          <span>🎵 Alerta sonora: {soundEnabled ? 'Activa' : 'Silenciada'}</span>
          <span className="font-bold text-seryi-green">Keyla CRM • v12 Realtime</span>
        </div>
      </div>
    </div>
  );
}
